import { determineThreadSizeDimensionUnit } from '@calculations/feature/alpha/utilities/dimension-unit';
import { UnreachableCaseError } from '@errors/unreachable';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  type FieldId,
  StaticDataSource,
  type ValueId,
} from '@snap-types/models/auto-configurator/state';

import { TRUE_VALUE_STRING } from '../../constants';
import { getLineItem } from '../../logic/line-item';
import { getQuantityBreak } from '../../logic/quantity-break';
import { getNarrowedStaticData } from '../../logic/static-data';
import {
  getValue,
  parseToDecimal,
  parseToNumber,
  resetValue,
} from '../../logic/value';
import { buildSelectChoice } from '../builders/select-choice';
import { lineItemDependencies } from '../dependencies/line-item-dependencies';
import { WritableAutoConfiguratorState } from '../slice';
import { applyValidation } from '../validation/apply-validation';
import { revalidateQuantity } from '../validation/revalidate-line-quantities';

type Payload = {
  fieldId: FieldId;
  valueId: ValueId;
  rawValue: string;
};

function isStaticDataSource(key: string): key is StaticDataSource {
  return Object.values(StaticDataSource).includes(key as StaticDataSource);
}

export function updateValue(
  state: WritableAutoConfiguratorState,
  action: PayloadAction<Payload>,
) {
  const { fieldId, valueId, rawValue } = action.payload;

  const valueObject = getValue(state, valueId);

  // Mark dirty and touched
  valueObject.isDirty = true;
  valueObject.isTouched = true;

  // Reset validity
  valueObject.isValid = true;
  valueObject.hasParseError = false;
  valueObject.errors = [];

  // Set raw value
  valueObject.rawValue = rawValue;

  // Parse value to native type
  const valueType = valueObject.type;
  let hasParseError = false;

  switch (valueType) {
    case 'decimal': {
      const trimmedValue = rawValue.trim();

      if (trimmedValue === '') {
        valueObject.value = null;
        break;
      }

      const result = parseToDecimal(trimmedValue);

      if (result.isOk()) {
        valueObject.value = result.value;
      } else {
        valueObject.value = null;
        valueObject.errors = [result.error];
        valueObject.hasParseError = true;
        hasParseError = true;
      }

      break;
    }

    case 'number': {
      const trimmedValue = rawValue.trim();

      if (trimmedValue === '') {
        valueObject.value = null;
        break;
      }

      const result = parseToNumber(trimmedValue);

      if (result.isOk()) {
        valueObject.value = result.value;
      } else {
        valueObject.value = null;
        valueObject.errors = [result.error];
        valueObject.hasParseError = true;
        hasParseError = true;
      }

      break;
    }

    case 'select': {
      if (rawValue === '') {
        valueObject.value = null;
      } else {
        valueObject.value = buildSelectChoice(rawValue);
      }

      break;
    }

    case 'text':
    case 'description': {
      const trimmedValue = rawValue.trim();

      valueObject.value = trimmedValue === '' ? null : trimmedValue;

      break;
    }

    case 'boolean': {
      valueObject.value = rawValue === TRUE_VALUE_STRING;

      break;
    }

    /* v8 ignore next 2 */
    default: {
      throw new UnreachableCaseError(valueType);
    }
  }

  applyValidation(state, fieldId, valueId, valueObject.errors, !hasParseError);

  revalidateAdjacentQuantityBreaks(state, valueId);

  // Manage dependencies
  if (valueId.model === 'lineItem') {
    const dependentKeys = lineItemDependencies[valueId.key] ?? [];

    for (const depKey of dependentKeys) {
      const depValueId = {
        model: 'lineItem' as const,
        key: depKey,
        uuid: valueId.uuid,
      };

      const depValue = getValue(state, depValueId);
      const hasValue = depValue.value !== null;
      const hadErrors = depValue.errors.length > 0;

      if (
        depValue.type === 'select' &&
        depValue.value?.value &&
        isStaticDataSource(depValueId.key)
      ) {
        const quoteLine = getLineItem(state, valueId.uuid);

        const depOptions = getNarrowedStaticData({
          state,
          name: depValueId.key as StaticDataSource,
          quoteLineUuid: quoteLine.uuid,
        });

        if (!depOptions.includes(depValue.value.value)) {
          resetValue(depValue);
        }
      } else {
        resetValue(depValue);
      }

      if (hasValue || hadErrors) {
        applyValidation(state, { model: 'lineItem', key: depKey }, depValueId);
      }
    }
  }
  // Set validity
  if (valueObject.errors.length > 0) {
    valueObject.isValid = false;
  }

  updateDimensionUnit(state, valueId, rawValue);
}

/**
 * Quantity ordering is validated between neighboring quantity breaks, so
 * editing one quantity can make (or fix) a violation on the break before or
 * after it. Those neighbors are not part of the edit, so revalidate them here
 * to surface or clear their errors.
 */
function revalidateAdjacentQuantityBreaks(
  state: WritableAutoConfiguratorState,
  valueId: ValueId,
) {
  if (valueId.model !== 'quantityBreak' || valueId.key !== 'quantity') {
    return;
  }

  const quantityBreak = getQuantityBreak(state, valueId.uuid);
  const lineItem = getLineItem(state, quantityBreak.lineItemUuid);
  const index = lineItem.quantityBreakUuids.indexOf(valueId.uuid);

  if (index < 0) {
    return;
  }

  for (const adjacentIndex of [index - 1, index + 1]) {
    if (adjacentIndex < 0) {
      continue;
    }

    const adjacentUuid = lineItem.quantityBreakUuids[adjacentIndex];

    if (!adjacentUuid) {
      continue;
    }

    revalidateQuantity(state, adjacentUuid);
  }
}

function updateDimensionUnit(
  state: WritableAutoConfiguratorState,
  valueId: ValueId,
  rawValue: string,
) {
  if (valueId.model === 'lineItem' && valueId.key === 'threadSize') {
    const lineItem = getLineItem(state, valueId.uuid);

    lineItem.dimensionUnit = determineThreadSizeDimensionUnit(rawValue);
  }
}
